import React, { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, Briefcase } from "lucide-react";
import { api } from "@/lib/apiClient";
import { PageHeader, Loader, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const empty = { title: "", category: "", rate: "", min_rate: 0, skills: "", summary: "", analysis: "", why_recommended: "", upwork_url: "", featured: false, published: true };

export default function AdminJobs() {
  const [jobs, setJobs] = useState(null);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [newCat, setNewCat] = useState("");

  const load = async () => {
    setJobs((await api.get("/admin/jobs")).data);
    setCats((await api.get("/job-categories")).data);
  };
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title || !form.upwork_url) return toast.error("Title and Upwork URL are required");
    const payload = { ...form, skills: typeof form.skills === "string" ? form.skills.split(",").map((s) => s.trim()).filter(Boolean) : form.skills };
    if (form.id) await api.put(`/admin/jobs/${form.id}`, payload);
    else await api.post("/admin/jobs", payload);
    toast.success("Saved"); setOpen(false); setForm(empty); load();
  };
  const edit = (j) => { setForm({ ...j, skills: (j.skills || []).join(", ") }); setOpen(true); };
  const del = async (id) => { await api.delete(`/admin/jobs/${id}`); toast.success("Deleted"); load(); };
  const addCat = async () => { if (!newCat) return; await api.post("/admin/job-categories", { name: newCat }); setNewCat(""); load(); };
  const delCat = async (id) => { await api.delete(`/admin/job-categories/${id}`); load(); };

  return (
    <div>
      <PageHeader title="Job Board" subtitle="Manage hand-picked Upwork opportunities"
        action={<Button className="rounded-full bg-teal hover:bg-teal/90" onClick={() => { setForm(empty); setOpen(true); }} data-testid="add-job"><Plus className="w-4 h-4 mr-1.5" /> Add Job</Button>} />

      <div className="bg-white border border-border rounded-2xl p-4 shadow-sm mb-4">
        <p className="font-medium text-sm mb-2">Categories</p>
        <div className="flex flex-wrap gap-2 items-center">
          {cats.map((c) => <span key={c.id} className="text-xs bg-secondary px-3 py-1.5 rounded-full flex items-center gap-1.5">{c.name}<button onClick={() => delCat(c.id)}><Trash2 className="w-3 h-3 text-red-500" /></button></span>)}
          <div className="flex gap-1">
            <Input className="h-8 w-36" placeholder="New category" value={newCat} onChange={(e) => setNewCat(e.target.value)} data-testid="new-cat-input" />
            <Button size="sm" variant="outline" className="rounded-full" onClick={addCat} data-testid="add-cat">Add</Button>
          </div>
        </div>
      </div>

      {!jobs ? <Loader /> : jobs.length === 0 ? (
        <EmptyState icon={Briefcase} title="No jobs yet" description="Add your first hand-picked Upwork opportunity." testId="admin-jobs-empty" />
      ) : (
        <div className="space-y-3">
          {jobs.map((j) => (
            <div key={j.id} className="bg-white border border-border rounded-2xl p-4 shadow-sm flex items-center justify-between gap-3" data-testid={`admin-job-${j.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium truncate">{j.title}</p>
                  {j.featured && <span className="text-xs text-warning">★</span>}
                  <StatusBadge status={j.published ? "approved" : "rejected"} />
                </div>
                <p className="text-xs text-muted-foreground">{j.category} · {j.rate}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="sm" variant="ghost" onClick={() => edit(j)} data-testid={`edit-job-${j.id}`}><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => del(j.id)} data-testid={`del-job-${j.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{form.id ? "Edit" : "Add"} Job</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title *</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="job-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Category</Label><Input value={form.category} onChange={(e) => set("category", e.target.value)} list="cats" data-testid="job-cat" />
                <datalist id="cats">{cats.map((c) => <option key={c.id} value={c.name} />)}</datalist></div>
              <div><Label>Rate</Label><Input value={form.rate} onChange={(e) => set("rate", e.target.value)} placeholder="$25/hr" data-testid="job-rate" /></div>
            </div>
            <div><Label>Minimum rate ($/hr, number)</Label><Input type="number" value={form.min_rate} onChange={(e) => set("min_rate", parseFloat(e.target.value) || 0)} placeholder="e.g. 6" data-testid="job-min-rate" />
              <p className="text-xs text-muted-foreground mt-1">$5 or below = free & applyable for everyone. $6+ = Apply locked for non-premium students.</p></div>
            <div><Label>Skills (comma separated)</Label><Input value={form.skills} onChange={(e) => set("skills", e.target.value)} data-testid="job-skills" /></div>
            <div><Label>Summary</Label><Textarea value={form.summary} onChange={(e) => set("summary", e.target.value)} data-testid="job-summary" /></div>
            <div><Label>Coach's Analysis</Label><Textarea value={form.analysis} onChange={(e) => set("analysis", e.target.value)} data-testid="job-analysis" /></div>
            <div><Label>Why Recommended</Label><Textarea value={form.why_recommended} onChange={(e) => set("why_recommended", e.target.value)} data-testid="job-why" /></div>
            <div><Label>Upwork URL *</Label><Input value={form.upwork_url} onChange={(e) => set("upwork_url", e.target.value)} placeholder="https://upwork.com/..." data-testid="job-url" /></div>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="job-featured" /> Featured</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.published} onCheckedChange={(v) => set("published", v)} data-testid="job-published" /> Published</label>
            </div>
          </div>
          <DialogFooter><Button className="rounded-full bg-teal hover:bg-teal/90" onClick={save} data-testid="save-job">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
